/**
 * Resolve a DOM element into a serializable capture. cssPath technique adapted
 * from RedLine src/overlay.js:120-137, anchored on Nova's data-testid convention.
 */
import { FEEDBACK_COMPUTED_STYLE_KEYS } from '@ic/platform-contract';
import type { ElementCapture, RectWithScroll } from './types';

const MAX_TEXT = 200;
const MAX_DEPTH = 5;

const esc = (v: string): string =>
  typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
    ? CSS.escape(v)
    : v.replace(/["\\]/g, String.raw`\$&`);

/** `data-testid` of an element; `undefined` when absent (or on a non-HTML/SVG/MathML element). */
function testIdOf(el: Element): string | undefined {
  return (el as Partial<HTMLElement>).dataset?.testid;
}

export function nearestTestId(el: Element): string | null {
  const hit = el.closest('[data-testid]');
  return hit ? (testIdOf(hit) ?? null) : null;
}

function segment(el: Element): string {
  let seg = el.tagName.toLowerCase();
  const role = el.getAttribute('role');
  const label = el.getAttribute('aria-label');
  if (role) seg += `[role="${esc(role)}"]`;
  else if (label) seg += `[aria-label="${esc(label)}"]`;
  const parent = el.parentElement;
  if (parent) {
    const same = Array.from(parent.children).filter((c) => c.tagName === el.tagName);
    if (same.length > 1) seg += `:nth-of-type(${same.indexOf(el) + 1})`;
  }
  return seg;
}

export function cssPath(el: Element): string {
  const parts: string[] = [];
  let cur: Element | null = el;
  let depth = 0;
  while (cur?.nodeType === 1 && depth < MAX_DEPTH) {
    const testId = testIdOf(cur);
    if (testId) {
      parts.unshift(`[data-testid="${esc(testId)}"]`);
      break;
    }
    if (cur.id) {
      parts.unshift(`#${esc(cur.id)}`);
      break;
    }
    if (cur === cur.ownerDocument.body) {
      parts.unshift('body');
      break;
    }
    parts.unshift(segment(cur));
    cur = cur.parentElement;
    depth += 1;
  }
  return parts.join(' > ');
}

export function rectWithScroll(el: Element): RectWithScroll {
  const r = el.getBoundingClientRect();
  return {
    left: r.left,
    top: r.top,
    width: r.width,
    height: r.height,
    scrollX: window.scrollX,
    scrollY: window.scrollY,
  };
}

export function resolveElement(el: Element): ElementCapture {
  const raw = (el as HTMLElement).innerText ?? el.textContent ?? '';
  const cs = getComputedStyle(el);
  const computedStyles: Record<string, string> = {};
  for (const key of FEEDBACK_COMPUTED_STYLE_KEYS) {
    const v = cs.getPropertyValue(key);
    if (v) computedStyles[key] = v;
  }
  return {
    selector: cssPath(el),
    testId: nearestTestId(el),
    tag: el.tagName.toLowerCase(),
    role: el.getAttribute('role'),
    ariaLabel: el.getAttribute('aria-label'),
    text: raw.trim().replace(/\s+/g, ' ').slice(0, MAX_TEXT),
    rect: rectWithScroll(el),
    computedStyles,
  };
}
